import { z } from 'zod'

import {
  CuratedListActionType,
  CuratedListType,
} from '../constants/curatedList'
import {
  MembershipLevelMax,
  MembershipLevelMin,
  MembershipLevelValue,
} from '../constants/membership'
import { listQueryZodFields } from './system'
import { dbEntitySchema } from './system'

const optionalDateInputSchema = z
  .union([z.date(), z.coerce.date(), z.number(), z.string()])
  .optional()
  .nullable()

export const curatedListSchema = dbEntitySchema.extend({
  city: z.string(),
  cityName: z.string().nullish(),
  cityImageUrl: z.string().nullish(),
  enable: z.boolean(),
  theme: z.string().nullish(),
  enableStart: z.date().nullish(),
  enableEnd: z.date().nullish(),
  membershipLevel: z
    .number()
    .min(MembershipLevelMin)
    .max(MembershipLevelMax)
    .nullish(),
  sort: z.number(),
  type: z.nativeEnum(CuratedListType),
  title: z.string(),
  description: z.string(),
  imageUrl: z.string().nullish(),
  thumbnailImageUrl: z.string().nullish(),
  authorName: z.string().nullish(),
  authorImageUrl: z.string().nullish(),
  itemCount: z.number().nullish(),
  followCount: z.number().nullish(),
})

const curatedListCreateArgsBaseSchema = z.object({
  cityId: z.string(),
  title: z.string().min(1),
  description: z.string().min(1),
  imageUrl: z.string().url(),
  thumbnailImageUrl: z.string().url().optional().nullable(),
  authorName: z.string().optional().nullable(),
  authorImageUrl: z.string().url().optional().nullable(),
  theme: z.string().optional().nullable(),
  enable: z.boolean().optional(),
  enableStart: optionalDateInputSchema,
  enableEnd: optionalDateInputSchema,
  membershipLevel: z.nativeEnum(MembershipLevelValue).optional().nullable(),
  type: z.nativeEnum(CuratedListType).optional(),
})

function refineDateRange<
  T extends { enableStart?: unknown; enableEnd?: unknown },
>(schema: z.ZodType<T>) {
  return schema.refine(
    (value) => {
      if (!value.enableStart || !value.enableEnd) return true
      const start = value.enableStart as Date | string | number
      const end = value.enableEnd as Date | string | number
      return new Date(start) < new Date(end)
    },
    {
      path: ['enableStart'],
      message: 'enableStart must be before enableEnd',
    }
  )
}

export const curatedListCreateArgsSchema = refineDateRange(
  curatedListCreateArgsBaseSchema
)

export const curatedListUpdateByIdArgsSchema = refineDateRange(
  curatedListCreateArgsBaseSchema.partial()
)

export const curatedListUpdateStatsByIdArgsSchema = z.object({
  itemCount: z.number().optional(),
})

export const curatedListActionUpdateSortSchema = z.object({
  type: z.literal(CuratedListActionType.UPDATE_SORT),
  sort: z.number(),
})

// input schemas
export const curatedListListQuerySchema = z.object({
  ...listQueryZodFields,
  city: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  type: z
    .union([
      z.nativeEnum(CuratedListType),
      z.array(z.nativeEnum(CuratedListType)),
    ])
    .optional()
    .nullable(),
  enable: z.coerce.boolean().optional().nullable(),
  membershipLevelMin: z.coerce.number().optional().nullable(),
  membershipLevelMax: z.coerce.number().optional().nullable(),
})

export const curatedListGetByIdParamsSchema = z.object({
  id: z.string(),
})

// response schemas
export const curatedListListResponseSchema = z.object({
  data: z.array(curatedListSchema),
  count: z.number(),
})
